import { useState, useEffect, useCallback } from "react";
import axios from "axios";

declare global {
  interface Window {
    EXPO_PUSH_TOKEN?: string;
    isExpoApp?: boolean;
  }
}

const API_URL = import.meta.env.VITE_API_URL;
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function translateError(message: string): string {
  if (message.includes("Push notifications are not supported") || message.includes("not supported")) {
    return "Push-уведомления не поддерживаются в этом браузере.";
  }
  if (message.includes("Notification permission denied") || message.includes("permission denied")) {
    return "Разрешение на уведомления не получено.";
  }
  if (message.includes("Not logged in") || message.includes("не авторизован")) {
    return "Пользователь не авторизован.";
  }
  if (message.includes("ServiceWorker script evaluation failed") || message.includes("evaluation failed")) {
    return "Не удалось зарегистрировать Service Worker – ошибка скрипта.";
  }
  if (message.includes("Failed to fetch") || message.includes("fetch")) {
    return "Ошибка сети при попытке подключения.";
  }
  if (message.includes("push service error") || message.includes("Registration failed") || message.includes("Push service error")) {
    return "Ошибка push-сервиса. Убедитесь, что используется HTTPS и браузер поддерживает push-уведомления.";
  }
  if (message.includes("VAPID_PUBLIC_KEY")) {
    return "VAPID ключ не настроен. Обратитесь к администратору.";
  }
  if (message.includes("Service Worker")) {
    return `Ошибка Service Worker: ${message}`;
  }
  if (message.includes("Ошибка сервера")) {
    return message;
  }
  
  return message === "Unknown error" ? "Неизвестная ошибка" : message;
}

export default function usePush() {
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isApp, setIsApp] = useState<boolean>(!!window.isExpoApp);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);

  const restoreSubscription = useCallback(async (registration: ServiceWorkerRegistration) => {
    if (!VAPID_PUBLIC_KEY) {
      console.error("[Push] Невозможно восстановить подписку: VAPID ключ не настроен");
      return;
    }

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        console.log("[Push] Пользователь не авторизован, восстановление отложено");
        return;
      }

      const permission = Notification.permission;
      if (permission !== "granted") {
        console.log("[Push] Разрешение на уведомления не получено");
        return;
      }

      const vapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey as BufferSource,
      });

      console.log("[Push] Подписка восстановлена, отправка на сервер...");
      await axios.post(`${API_URL}/user/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setIsSubscribed(true);
      console.log("[Push] Подписка успешно восстановлена!");
    } catch (error: any) {
      console.error("[Push] Ошибка восстановления подписки:", error);
      setIsSubscribed(false);
    }
  }, []);

  const checkAndRestoreSubscription = useCallback(async () => {
    if (window.isExpoApp) return;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        const isActive = !!subscription.endpoint;
        setIsSubscribed(isActive);
        
        if (!isActive) {
          console.log("[Push] Подписка найдена, но неактивна. Попытка восстановления...");
          await restoreSubscription(registration);
        } else {
          console.log("[Push] Подписка активна");
        }
      } else {
        setIsSubscribed(false);
        console.log("[Push] Подписка не найдена");
      }
    } catch (error) {
      console.error("[Push] Ошибка при проверке подписки:", error);
      setIsSubscribed(false);
    }
  }, [restoreSubscription]);

  useEffect(() => {
    if (window.isExpoApp) {
      setIsApp(true);
      setIsSubscribed(!!window.EXPO_PUSH_TOKEN);
      return;
    }

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("[Push] Service Worker или PushManager не поддерживаются");
      return;
    }

    console.log("[Push] Проверка существующей подписки при загрузке...");
    checkAndRestoreSubscription();

    const checkInterval = setInterval(() => {
      console.log("[Push] Периодическая проверка подписки...");
      checkAndRestoreSubscription();
    }, 5 * 60 * 1000);

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
        console.log("[Push] Получено сообщение об изменении подписки от SW");
        
        if (event.data.newSubscription) {
          const authToken = localStorage.getItem("authToken");
          if (authToken) {
            try {
              await axios.post(`${API_URL}/user/subscribe`, event.data.newSubscription, {
                headers: { Authorization: `Bearer ${authToken}` },
              });
              console.log("[Push] Новая подписка отправлена на сервер");
              setIsSubscribed(true);
            } catch (error) {
              console.error("[Push] Ошибка отправки новой подписки на сервер:", error);

              try {
                const registration = await navigator.serviceWorker.ready;
                await restoreSubscription(registration);
              } catch (restoreError) {
                console.error("[Push] Ошибка восстановления подписки:", restoreError);
                setIsSubscribed(false);
              }
            }
          }
        } else {
          console.log("[Push] Новая подписка не найдена, восстановление...");
          try {
            const registration = await navigator.serviceWorker.ready;
            await restoreSubscription(registration);
          } catch (restoreError) {
            console.error("[Push] Ошибка восстановления подписки:", restoreError);
            setIsSubscribed(false);
          }
        }
      } else if (event.data?.type === "PUSH_SUBSCRIPTION_ERROR") {
        console.error("[Push] Ошибка подписки в SW:", event.data.error);
        setIsSubscribed(false);

        try {
          const registration = await navigator.serviceWorker.ready;
          await restoreSubscription(registration);
        } catch (restoreError) {
          console.error("[Push] Ошибка восстановления подписки после ошибки SW:", restoreError);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    const handleFocus = () => {
      console.log("[Push] Окно получило фокус, проверка подписки...");
      checkAndRestoreSubscription();
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(checkInterval);
      navigator.serviceWorker.removeEventListener("message", handleMessage);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkAndRestoreSubscription, restoreSubscription]);

  const subscribeWeb = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (window.isExpoApp) {
        setIsLoading(false);
        return;
      }

      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        const errorMsg = "Push notifications are not supported on this browser.";
        console.error("[Push] Ошибка:", errorMsg);
        throw new Error(errorMsg);
      }

      if (location.protocol !== "https:" && location.hostname !== "localhost" && location.hostname !== "127.0.0.1") {
        const errorMsg = "Push-уведомления требуют HTTPS соединения.";
        console.error("[Push] Ошибка:", errorMsg);
        throw new Error(errorMsg);
      }

      if (!VAPID_PUBLIC_KEY) {
        const errorMsg = "VAPID_PUBLIC_KEY не настроен. Проверьте переменные окружения.";
        console.error("[Push] Критическая ошибка:", errorMsg);
        throw new Error(errorMsg);
      }

      console.log("[Push] Начало регистрации Service Worker...");
      
      let registration;
      try {
        registration = await navigator.serviceWorker.register(
          "/service-worker.js",
          { scope: "/" }
        );
        console.log("[Push] Service Worker зарегистрирован:", registration.scope);
      } catch (swError: any) {
        console.error("[Push] Ошибка регистрации Service Worker:", swError);
        const errorMsg = swError?.message || "Failed to register Service Worker";

        if (errorMsg.includes("evaluation failed") || errorMsg.includes("script evaluation")) {
          throw new Error("Service Worker script evaluation failed - проверьте синтаксис service-worker.js");
        }
        if (errorMsg.includes("push service error") || errorMsg.includes("Registration failed")) {
          throw new Error("Push service error - возможно, браузер не поддерживает push или требуется HTTPS");
        }
        throw new Error(`Ошибка регистрации Service Worker: ${errorMsg}`);
      }

      await navigator.serviceWorker.ready;
      console.log("[Push] Service Worker готов");

      try {
        await registration.update();
        console.log("[Push] Service Worker обновлен");
      } catch (updateError) {
        console.warn("[Push] Предупреждение при обновлении Service Worker:", updateError);
      }

      console.log("[Push] Запрос разрешения на уведомления...");
      const permission = await Notification.requestPermission();
      console.log("[Push] Разрешение:", permission);
      
      if (permission !== "granted") {
        const errorMsg = "Notification permission denied.";
        console.error("[Push] Ошибка:", errorMsg);
        throw new Error(errorMsg);
      }

      console.log("[Push] Проверка существующей подписки...");
      let subscription = await registration.pushManager.getSubscription();

      if (subscription && subscription.endpoint) {
        console.log("[Push] Используется существующая активная подписка");
      } else {
        if (subscription && !subscription.endpoint) {
          console.log("[Push] Подписка найдена, но неактивна. Создание новой...");
          try {
            await subscription.unsubscribe();
          } catch (e) {
            console.warn("[Push] Не удалось отписаться от старой подписки:", e);
          }
        } else {
          console.log("[Push] Подписка не найдена. Создание новой...");
        }

        try {
          const vapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey as BufferSource,
          });
          console.log("[Push] Подписка создана успешно");
        } catch (subscribeError: any) {
          console.error("[Push] Ошибка создания подписки:", subscribeError);
          const errorMsg = subscribeError?.message || "Failed to subscribe to push";
          if (errorMsg.includes("push service error") || errorMsg.includes("Registration failed")) {
            throw new Error("Push service error - возможно, требуется HTTPS или браузер не поддерживает push");
          }
          throw new Error(`Ошибка подписки: ${errorMsg}`);
        }
      }

      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        const errorMsg = "Not logged in";
        console.error("[Push] Ошибка:", errorMsg);
        throw new Error(errorMsg);
      }

      console.log("[Push] Отправка подписки на сервер...");
      try {
        await axios.post(`${API_URL}/user/subscribe`, subscription.toJSON(), {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        console.log("[Push] Подписка успешно отправлена на сервер");
      } catch (serverError: any) {
        console.error("[Push] Ошибка отправки на сервер:", serverError);
        throw new Error(`Ошибка сервера: ${serverError?.response?.data?.message || serverError?.message || "Unknown server error"}`);
      }

      setIsSubscribed(true);
      setIsLoading(false);
      setError(null);
      console.log("[Push] Подписка завершена успешно!");
    } catch (err: any) {
      setIsLoading(false);
      console.error("[Push] Общая ошибка:", err);
      console.error("[Push] Детали ошибки:", {
        message: err?.message,
        stack: err?.stack,
        name: err?.name,
        fullError: err
      });
      const translated = translateError(err?.message || "Unknown error");
      setError(translated);
    }
  }, []);

  const subscribe = useCallback(async () => {
    await subscribeWeb();
  }, [subscribeWeb]);

  return {
    subscribe,
    isSubscribed,
    isApp,
    isLoading,
    error,
  };
}
